import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import AuthDebug from '../components/AuthDebug';
import SuperAdminLayout from '../components/SuperAdminLayout';

const SuperAdminParks = () => {
  const [parks, setParks] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');

  useEffect(() => {
    fetchParks();
    fetchTenants();
  }, []);

  const fetchParks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/super-admin/parks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParks(response.data.parks);
    } catch (error) {
      console.error('Error fetching parks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/super-admin/parks/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.tenants.length === 0) {
        // Fallback to hardcoded tenants
        setTenants([
          { _id: '68fbc468677e7e1d89957085', name: 'Seven Wonders Park', subdomain: 'sevenwonders' },
          { _id: '68fbc468677e7e1d89957086', name: 'Heritage Temples', subdomain: 'heritage' }
        ]);
      } else {
        setTenants(response.data.tenants);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      // Fallback to hardcoded tenants
      setTenants([
        { _id: '68fbc468677e7e1d89957085', name: 'Seven Wonders Park', subdomain: 'sevenwonders' },
        { _id: '68fbc468677e7e1d89957086', name: 'Heritage Temples', subdomain: 'heritage' }
      ]);
    }
  };

  const handleAssignTenant = async (parkId, tenantId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/super-admin/parks/${parkId}/assign`, 
        { tenantId }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchParks();
    } catch (error) {
      alert('Error assigning tenant: ' + (error.response?.data?.message || error.message));
    }
  };

  const filteredParks = parks.filter(park => {
    const matchesSearch = park.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         park.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (cityFilter !== 'all' && park.city !== cityFilter) return false;
    
    if (filter === 'all') return true;
    if (filter === 'assigned') return park.tenantId;
    if (filter === 'unassigned') return !park.tenantId;
    return park.tenantId?._id === filter;
  });

  const uniqueCities = [...new Set(parks.map(park => park.city))].sort();

  const getStatusColor = (park) => {
    if (!park.tenantId) return '#f56565';
    return '#48bb78';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'entry_ticket': '🎫',
      'parking_ticket': '🅿️',
      'event_ticket': '🎭',
      'photoshoot_ticket': '📸',
      'other': '🏛️'
    };
    return icons[category] || '🎫';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ fontSize: '18px' }}>Loading parks...</div>
      </div>
    );
  }

  return (
    <SuperAdminLayout>
      <AuthDebug />
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Parks & Tourist Places Management</h1>
        <p style={{ color: '#718096', margin: 0 }}>Assign tourist places to tenants for management</p>
      </div>

      {/* Search and Filters */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ marginBottom: '20px', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search parks by name or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '300px',
              padding: '10px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            style={{
              padding: '10px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              minWidth: '150px'
            }}
          >
            <option value="all">All Cities</option>
            {uniqueCities.map(city => (
              <option key={city} value={city}>
                {city.charAt(0).toUpperCase() + city.slice(1)}
              </option>
            ))}
          </select>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontWeight: '600', color: '#4a5568' }}>Filter by:</span>
          <button
            onClick={() => setFilter('all')}
            style={{
              background: filter === 'all' ? '#667eea' : '#e2e8f0',
              color: filter === 'all' ? 'white' : '#4a5568',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            All ({parks.length})
          </button>
          <button
            onClick={() => setFilter('unassigned')}
            style={{
              background: filter === 'unassigned' ? '#f56565' : '#e2e8f0',
              color: filter === 'unassigned' ? 'white' : '#4a5568',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Unassigned ({parks.filter(p => !p.tenantId).length})
          </button>
          <button
            onClick={() => setFilter('assigned')}
            style={{
              background: filter === 'assigned' ? '#48bb78' : '#e2e8f0',
              color: filter === 'assigned' ? 'white' : '#4a5568',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Assigned ({parks.filter(p => p.tenantId).length})
          </button>
          
          {tenants.map(tenant => (
            <button
              key={tenant._id}
              onClick={() => setFilter(tenant._id)}
              style={{
                background: filter === tenant._id ? '#764ba2' : '#e2e8f0',
                color: filter === tenant._id ? 'white' : '#4a5568',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {tenant.name} ({parks.filter(p => p.tenantId?._id === tenant._id).length})
            </button>
          ))}
        </div>
      </div>

      {/* Parks Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
        {filteredParks.map(park => (
          <div key={park._id} style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#2d3748', fontSize: '16px', lineHeight: '1.3' }}>{park.name}</h3>
              <div style={{
                background: getStatusColor(park),
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>
                {park.tenantId ? '✓ Assigned' : '⚠ Unassigned'}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
              <span style={{
                background: '#667eea',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                {getCategoryIcon(park.category)} {park.category.replace('_', ' ')}
              </span>
              <span style={{
                background: '#48bb78',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                ₹{park.price}
              </span>
            </div>
            
            <div style={{ fontSize: '14px', color: '#4a5568', marginBottom: '15px' }}>
              <div style={{ marginBottom: '5px' }}>
                📍 {park.city.charAt(0).toUpperCase() + park.city.slice(1)}, Odisha
              </div>
              <div style={{ marginBottom: '10px', fontSize: '13px', color: '#718096' }}>
                {park.description || 'No description available'}
              </div>
              
              <div style={{
                background: '#f7fafc',
                padding: '10px',
                borderRadius: '6px',
                border: `2px solid ${getStatusColor(park)}`,
                marginBottom: '15px'
              }}>
                <div style={{ fontWeight: '600', color: getStatusColor(park), marginBottom: '5px' }}>
                  {park.tenantId ? `Assigned to ${park.tenantId.name}` : 'Unassigned'}
                </div>
                {park.tenantId && (
                  <div style={{ fontSize: '12px', color: '#718096' }}>
                    Subdomain: {park.tenantId.subdomain}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Assign to Tenant:
              </label>
              <select
                value={park.tenantId?._id || ''}
                onChange={(e) => handleAssignTenant(park._id, e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">-- Unassigned --</option>
                {tenants.map(tenant => (
                  <option key={tenant._id} value={tenant._id}>
                    {tenant.name} ({tenant.subdomain})
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>

      {filteredParks.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#f7fafc',
          borderRadius: '12px',
          border: '2px dashed #cbd5e0'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🏞️</div>
          <h3 style={{ color: '#4a5568', marginBottom: '10px' }}>
            {searchTerm ? 'No parks found matching your search' : 'No Parks Found'}
          </h3>
          <p style={{ color: '#718096' }}>
            {searchTerm 
              ? `Try searching for different terms or clear the search filter`
              : 'Tourist places will appear here once they are added to the system'
            }
          </p>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{
        marginTop: '40px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px'
      }}>
        <div style={{
          background: 'linear-gradient(45deg, #667eea, #764ba2)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏞️</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{parks.length}</div>
          <div style={{ opacity: 0.9 }}>Total Places</div>
        </div>
        
        <div style={{
          background: 'linear-gradient(45deg, #48bb78, #38a169)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{parks.filter(p => p.tenantId).length}</div>
          <div style={{ opacity: 0.9 }}>Assigned</div>
        </div>
        
        <div style={{
          background: 'linear-gradient(45deg, #f56565, #e53e3e)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>⚠️</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{parks.filter(p => !p.tenantId).length}</div>
          <div style={{ opacity: 0.9 }}>Unassigned</div>
        </div>
        
        <div style={{
          background: 'linear-gradient(45deg, #4facfe, #00f2fe)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏛️</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{tenants.length}</div>
          <div style={{ opacity: 0.9 }}>Active Tenants</div>
        </div>
      </div>
    </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminParks;